/**
 * humanize.js — Enum humanization utility (REQ-SYS-VOCAB-01)
 *
 * Single source of truth for converting raw enum/status tokens
 * into human-readable display labels.
 *
 * Usage:
 *   import { humanizeEnum, humanizeStatus, humanizeUnit } from '../utils/humanize';
 *   <span>{humanizeEnum('residential_new')}</span>  // "Residential (New)"
 *   <span>{humanizeStatus('in_progress')}</span>    // "In Progress"
 *
 * Rule: Apply at the DISPLAY BOUNDARY only. Data layer keeps raw tokens.
 */

// ─── Canonical enum → human label map ────────────────────────────────────────
const ENUM_LABELS = {
  // Statuses
  active: 'Active',
  inactive: 'Inactive',
  pending: 'Pending',
  draft: 'Active', // pack rule 4: quote lifecycle has no Draft; legacy backend values map to Active
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  expired: 'Expired',
  completed: 'Completed',
  in_progress: 'In Progress',
  partially_scheduled: 'Partially Scheduled',
  not_scheduled: 'Not Scheduled',
  on_hold: 'On Hold',
  cancelled: 'Cancelled',
  approved: 'Approved',
  rejected: 'Rejected',
  lost: 'Lost',
  won: 'Won',
  open: 'Open',
  closed: 'Closed',
  archived: 'Archived',
  scheduled: 'Scheduled',
  accepted: 'Accepted',
  signed: 'Signed',
  not_started: 'Not Started',
  past_due: 'Past Due',
  awaiting: 'Awaiting',
  review: 'Review',

  // Signature statuses
  acknowledged: 'Signed',
  acknowledged_pending: 'Pending',
  acknowledged_pending_expired: 'Expired', // IDI-787: a link that ran out unsigned
  pending_signature: 'Awaiting',
  pending_expired: 'Pending',

  // Property types
  residential: 'Residential',
  residential_new: 'Residential (New)',
  residential_existing: 'Residential (Existing)',
  commercial: 'Commercial',
  commercial_new: 'Commercial (New)',
  commercial_existing: 'Commercial (Existing)',
  industrial: 'Industrial',
  multi_family: 'Multi-Family',

  // Units of measure
  sq_ft: 'Sq Ft',
  sqft: 'Sq Ft',
  square_feet: 'Square Feet',
  linear_ft: 'Linear Ft',
  linear_feet: 'Linear Feet',
  each: 'Each',
  per_unit: 'Per Unit',
  lump_sum: 'Lump Sum',
  hourly: 'Hourly',
  per_board_foot: 'Per Board Foot',
  board_foot: 'Board Foot',
  board_feet: 'Board Feet',

  // Lead sources
  referral: 'Referral',
  website: 'Website',
  google: 'Google',
  social_media: 'Social Media',
  word_of_mouth: 'Word of Mouth',
  repeat_customer: 'Repeat Customer',
  home_show: 'Home Show',
  other: 'Other',
  direct: 'Direct',
  advertisement: 'Advertisement',

  // Job types
  new_construction: 'New Construction',
  remodel: 'Remodel',
  repair: 'Repair',
  service: 'Service',
  maintenance: 'Maintenance',
  inspection: 'Inspection',
  retrofit: 'Retrofit',
  insulation: 'Insulation',

  // Payment terms
  net_30: 'Net 30',
  net_15: 'Net 15',
  net_60: 'Net 60',
  due_on_receipt: 'Due on Receipt',
  cod: 'COD',
  upon_completion: 'Upon Completion',

  // Work order types
  change_order: 'Change Order',
  punch_list: 'Punch List',
  warranty: 'Warranty',

  // Roles
  admin: 'Admin',
  field_tech: 'Field Tech',
  office_staff: 'Office Staff',
  estimator: 'Estimator',
  project_manager: 'Project Manager',

  // Quote-specific
  converted: 'Converted', // Note: StatusPill will NOT render this, but label exists for data exports
};

/**
 * Humanize any enum/status string for display.
 * Checks the canonical map first, then falls back to prettify.
 */
export function humanizeEnum(value) {
  if (!value) return '';
  const key = String(value).toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (ENUM_LABELS[key]) return ENUM_LABELS[key];
  return prettify(value);
}

/**
 * Alias for humanizeEnum — specifically for status values.
 */
export function humanizeStatus(value) {
  return humanizeEnum(value);
}

/**
 * Humanize a unit of measure.
 */
export function humanizeUnit(value) {
  return humanizeEnum(value);
}

/**
 * Humanize a property/project type.
 */
export function humanizeType(value) {
  return humanizeEnum(value);
}

/**
 * Humanize a lead source.
 */
export function humanizeSource(value) {
  return humanizeEnum(value);
}

/**
 * Fallback prettify: replace underscores/hyphens with spaces, title-case each word.
 */
function prettify(s) {
  return String(s)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default humanizeEnum;
